// The corporate records book — the corporation's own governance record.
//
// Laid out tab for tab like a purchased corporate kit, because that is what a lawyer,
// a lender or an auditor expects to be handed. The database is the system of record;
// this page is its bound form, and the print view is what goes in the binder.
//
// Managing broker only, enforced on the server as well: a stock ledger and officer
// compensation are not staff-readable material.

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import DataList from '../../components/shared/DataList';

const TABS = [
    { key: 'overview',      n: '',  label: 'Index' },
    { key: 'formation',     n: '1', label: 'Formation' },
    { key: 'bylaws',        n: '2', label: 'By-Laws' },
    { key: 'minutes',       n: '3', label: 'Minutes' },
    { key: 'shares',        n: '4', label: 'Shares' },
    { key: 'resolutions',   n: '5', label: 'Resolutions' },
    { key: 'banking',       n: '6', label: 'Banking' },
    { key: 'state_filings', n: '7', label: 'State filings' },
    { key: 'tax',           n: '8', label: 'Tax' },
];

const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '—';
const Field = ({ label, value }) => (
    <div className="py-2 border-b border-slate-100">
        <dt className="text-xs uppercase tracking-wide text-slate-400">{label}</dt>
        <dd className="text-slate-800">{value || <span className="text-slate-400">not on file</span>}</dd>
    </div>
);

const Modal = ({ title, onClose, children }) => (
    <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 p-6 overflow-y-auto">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl my-8">
            <div className="flex justify-between items-center px-6 py-4 border-b">
                <h3 className="font-semibold text-slate-800">{title}</h3>
                <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none">&times;</button>
            </div>
            <div className="p-6">{children}</div>
        </div>
    </div>
);

const Input = ({ label, ...props }) => (
    <label className="block mb-3">
        <span className="text-sm text-slate-600">{label}</span>
        <input {...props} className="mt-1 w-full p-2 border rounded" />
    </label>
);

export default function CorporateRecordsPage() {
    const apiClient = useApiClient();
    const [tab, setTab] = useState('overview');
    const [binder, setBinder] = useState(null);
    const [obligations, setObligations] = useState([]);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState({});

    const load = useCallback(async () => {
        try {
            setError('');
            const [b, o] = await Promise.all([
                apiClient.get('/api/corporate/binder').catch(e => {
                    if (String(e.message).includes('No corporation')) return null;
                    throw e;
                }),
                apiClient.get('/api/corporate/obligations').catch(() => []),
            ]);
            setBinder(b);
            setObligations(o || []);
        } catch (e) { setError(e.message); }
    }, [apiClient]);

    useEffect(() => { load(); }, [load]);

    const submit = async (url, body, method = 'post') => {
        setBusy(true);
        try {
            const res = await apiClient[method](url, body);
            if (res?.warnings?.length) setError(res.warnings.join(' '));
            setModal(null); setForm({});
            await load();
        } catch (e) { setError(e.message); }
        finally { setBusy(false); }
    };

    const completeObligation = async (id) => {
        setBusy(true);
        try { await apiClient.post(`/api/corporate/obligations/${id}/complete`, {}); await load(); }
        catch (e) { setError(e.message); }
        finally { setBusy(false); }
    };

    // ---- not set up yet -------------------------------------------------
    if (binder === null) {
        return (
            <div className="p-6 max-w-3xl">
                <h1 className="text-2xl font-bold text-slate-800 mb-1">Corporate Records Book</h1>
                <p className="text-slate-500 mb-6">
                    The corporation's own governance record — articles, by-laws, minutes, the stock
                    ledger, officers, and the filings the State and the IRS expect.
                </p>
                {error && <p className="mb-4 text-red-600">{error}</p>}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                    <h2 className="font-semibold text-amber-900 mb-2">No corporation is recorded yet</h2>
                    <p className="text-amber-800 mb-4 text-sm">
                        Every value below must come from a filed document, not from memory — a lender,
                        a landlord or an auditor relies on what this book says. Have the articles of
                        incorporation to hand before you begin.
                    </p>
                    <button onClick={() => { setForm({ state: 'IL', fiscal_year_end: '12-31', annual_meeting_md: '12-15' }); setModal('corp'); }}
                        className="px-4 py-2 bg-amber-700 text-white rounded-md">Record the corporation</button>
                </div>

                {modal === 'corp' && (
                    <Modal title="Record the corporation" onClose={() => setModal(null)}>
                        <Input label="Legal name, exactly as filed" value={form.legal_name || ''}
                            onChange={e => setForm({ ...form, legal_name: e.target.value })} />
                        <Input label="Short code (e.g. BREC)" value={form.code || ''}
                            onChange={e => setForm({ ...form, code: e.target.value })} />
                        <Input label="Secretary of State file number" value={form.state_file_number || ''}
                            onChange={e => setForm({ ...form, state_file_number: e.target.value })} />
                        <Input label="Date of incorporation" type="date" value={form.incorporated_on || ''}
                            onChange={e => setForm({ ...form, incorporated_on: e.target.value })} />
                        <Input label="Registered agent" value={form.registered_agent || ''}
                            onChange={e => setForm({ ...form, registered_agent: e.target.value })} />
                        <Input label="Registered office" value={form.registered_office || ''}
                            onChange={e => setForm({ ...form, registered_office: e.target.value })} />
                        <Input label="Shares authorised" type="number" value={form.shares_authorized || ''}
                            onChange={e => setForm({ ...form, shares_authorized: e.target.value })} />
                        <p className="text-xs text-slate-500 mb-3">
                            The EIN is not entered here — it lives on the entity record used by payroll
                            and tax filings, so there is only ever one employer number.
                        </p>
                        <button disabled={busy} onClick={() => submit('/api/corporate', form)}
                            className="px-4 py-2 bg-slate-800 text-white rounded-md disabled:opacity-50">
                            {busy ? 'Saving…' : 'Record it'}
                        </button>
                    </Modal>
                )}
            </div>
        );
    }

    if (!binder) return <div className="p-6 text-slate-500">Loading the book…</div>;

    const corp = binder.corporation;
    const tabData = (key) => binder.tabs.find(t => t.key === key) || {};
    const overdue = obligations.filter(o => o.days_until !== null && o.days_until < 0);
    const dueSoon = obligations.filter(o => o.days_until !== null && o.days_until >= 0 && o.days_until <= 60);

    return (
        <div className="p-6 max-w-6xl">
            <div className="flex justify-between items-start mb-1">
                <h1 className="text-2xl font-bold text-slate-800">Corporate Records Book</h1>
                <Link to="/dashboard/corporate-book"
                    className="text-sm px-3 py-1.5 border rounded-md text-slate-600 no-print hover:bg-slate-50">
                    Open the bound book
                </Link>
            </div>
            <p className="text-slate-500 mb-6">
                {corp.legal_name}{corp.state_file_number ? ` · File ${corp.state_file_number}` : ''}
                {corp.ein ? ` · EIN ${corp.ein}` : ' · EIN not on file'}
            </p>

            {error && <p className="mb-4 text-red-600 bg-red-50 border border-red-200 rounded p-3 text-sm">{error}</p>}

            {(overdue.length > 0 || dueSoon.length > 0) && (
                <div className="mb-6 grid gap-2">
                    {overdue.map(o => (
                        <div key={o.obligation_id} className="flex justify-between items-center bg-red-50 border border-red-200 rounded px-4 py-2">
                            <span className="text-sm text-red-900">
                                <strong>{o.title}</strong> — overdue by {Math.abs(o.days_until)} days ({fmtDate(o.next_due)})
                            </span>
                            <button onClick={() => completeObligation(o.obligation_id)} disabled={busy}
                                className="text-xs px-3 py-1 bg-red-700 text-white rounded no-print">Mark filed</button>
                        </div>
                    ))}
                    {dueSoon.map(o => (
                        <div key={o.obligation_id} className="flex justify-between items-center bg-amber-50 border border-amber-200 rounded px-4 py-2">
                            <span className="text-sm text-amber-900">
                                <strong>{o.title}</strong> — due in {o.days_until} days ({fmtDate(o.next_due)})
                            </span>
                            <button onClick={() => completeObligation(o.obligation_id)} disabled={busy}
                                className="text-xs px-3 py-1 bg-amber-700 text-white rounded no-print">Mark filed</button>
                        </div>
                    ))}
                </div>
            )}

            <div className="flex gap-6">
                {/* tab dividers */}
                <nav className="w-44 flex-shrink-0 no-print">
                    {TABS.map(t => (
                        <button key={t.key} onClick={() => setTab(t.key)}
                            className={`w-full text-left px-3 py-2 mb-1 rounded text-sm border-l-4 ${
                                tab === t.key
                                    ? 'bg-slate-800 text-white border-amber-500'
                                    : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}>
                            {t.n && <span className="text-xs opacity-60 mr-2">{t.n}</span>}{t.label}
                        </button>
                    ))}
                </nav>

                <div className="flex-1 bg-white rounded-lg shadow p-6 min-w-0">
                    {tab === 'overview' && (
                        <>
                            <h2 className="text-lg font-semibold text-slate-800 mb-4">Index</h2>
                            <dl className="mb-6">
                                <Field label="Legal name" value={corp.legal_name} />
                                <Field label="State of incorporation" value={corp.state} />
                                <Field label="File number" value={corp.state_file_number} />
                                <Field label="EIN" value={corp.ein} />
                                <Field label="Incorporated" value={fmtDate(corp.incorporated_on)} />
                                <Field label="Fiscal year end" value={corp.fiscal_year_end} />
                                <Field label="Registered agent" value={corp.registered_agent} />
                                <Field label="Registered office" value={corp.registered_office} />
                                <Field label="Shares authorised" value={corp.shares_authorized?.toLocaleString()} />
                                <Field label="Shares issued" value={corp.shares_issued?.toLocaleString()} />
                                <Field label="Annual meeting held" value={corp.annual_meeting_md} />
                            </dl>

                            <div className="flex justify-between items-center mb-3">
                                <h3 className="font-semibold text-slate-800">Officers and directors</h3>
                                <button onClick={() => { setForm({ role_type: 'officer' }); setModal('officer'); }}
                                    className="text-sm px-3 py-1 border rounded no-print">Add</button>
                            </div>
                            {binder.officers.length === 0
                                ? <p className="text-slate-400 text-sm">No officers recorded.</p>
                                : (
                                    <table className="w-full text-sm">
                                        <tbody>
                                            {binder.officers.map(o => (
                                                <tr key={o.officer_id} className="border-b border-slate-100">
                                                    <td className="py-2 font-medium text-slate-800">{o.office}</td>
                                                    <td className="py-2">{o.person}</td>
                                                    <td className="py-2 text-slate-500">{o.role_type}</td>
                                                    <td className="py-2 text-slate-500">{fmtDate(o.elected_on)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                        </>
                    )}

                    {tab === 'minutes' && (
                        <>
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-lg font-semibold text-slate-800">Minutes</h2>
                                <button onClick={() => { setForm({ meeting_type: 'annual', status: 'held' }); setModal('meeting'); }}
                                    className="text-sm px-3 py-1 border rounded no-print">Record a meeting</button>
                            </div>
                            <p className="text-xs text-slate-500 mb-4">
                                A meeting whose date has not arrived is recorded as scheduled, never as held.
                                A book with an honest gap is worth more than one with an invented entry.
                            </p>
                            {(tabData('minutes').meetings || []).length === 0
                                ? <p className="text-slate-400 text-sm">No meetings recorded.</p>
                                : (tabData('minutes').meetings || []).map(m => (
                                    <div key={m.meeting_id} className="mb-5 pb-5 border-b border-slate-100 last:border-0">
                                        <div className="flex justify-between items-baseline">
                                            <h3 className="font-semibold text-slate-800">
                                                {fmtDate(m.held_on)} — {m.meeting_type.replace('_', ' ')} meeting
                                            </h3>
                                            <span className={`text-xs px-2 py-0.5 rounded ${
                                                m.status === 'held' ? 'bg-green-100 text-green-800'
                                                : m.status === 'scheduled' ? 'bg-amber-100 text-amber-800'
                                                : 'bg-slate-100 text-slate-600'}`}>{m.status}</span>
                                        </div>
                                        {m.fiscal_year && <p className="text-xs text-slate-500">Reporting on fiscal year {m.fiscal_year}</p>}
                                        {m.summary && <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{m.summary}</p>}
                                        {m.resolutions?.length > 0 && (
                                            <ul className="mt-2 text-sm list-disc pl-5 text-slate-700">
                                                {m.resolutions.map(r => <li key={r.resolution_id}>{r.title}</li>)}
                                            </ul>
                                        )}
                                    </div>
                                ))}
                        </>
                    )}

                    {tab === 'resolutions' && (
                        <>
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-lg font-semibold text-slate-800">Resolutions</h2>
                                <button onClick={() => { setForm({ resolution_type: 'other', status: 'adopted' }); setModal('resolution'); }}
                                    className="text-sm px-3 py-1 border rounded no-print">Record a resolution</button>
                            </div>
                            <p className="text-xs text-slate-500 mb-4">
                                Standalone resolutions, dated to the documents they authorise. Business done
                                at a meeting is printed under Minutes. A lease is authorised once, when it is
                                entered into — not re-authorised every year.
                            </p>
                            {(tabData('resolutions').index || []).length === 0
                                ? <p className="text-slate-400 text-sm">No standalone resolutions recorded.</p>
                                : (
                                    <DataList rows={tabData('resolutions').index || []} rowKey="resolution_id" csvName="corporate-resolutions" defaultSort={{ key: 'adopted_on', dir: 'desc' }}
                                        filters={[{ key: 'resolution_type', label: 'Type' }, { key: 'status', label: 'Status' }]}
                                        columns={[
                                            { key: 'adopted_on', label: 'Adopted', type: 'date', render: (r) => <span className="whitespace-nowrap">{fmtDate(r.adopted_on)}</span> },
                                            { key: 'title', label: 'Title', value: (r) => `${r.title}${r.document_ref ? ` (${r.document_ref})` : ''}`, sortValue: (r) => r.title,
                                                render: (r) => <>{r.title}{r.document_ref && <div className="text-xs text-slate-400">{r.document_ref}</div>}</> },
                                            { key: 'resolution_type', label: 'Type' },
                                            { key: 'status', label: 'Status' },
                                        ]} />
                                )}
                        </>
                    )}

                    {tab === 'shares' && (
                        <>
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-lg font-semibold text-slate-800">Stock transfer ledger</h2>
                                <button onClick={() => { setForm({ txn_type: 'issue' }); setModal('share'); }}
                                    className="text-sm px-3 py-1 border rounded no-print">Record a certificate</button>
                            </div>
                            <p className="text-xs text-slate-500 mb-4">
                                The record of shareholders required by 805 ILCS 5/7.75. Cancelled certificates
                                stay in the ledger with their surrender date — this is the history of the
                                shares, not a list of who holds them today.
                            </p>
                            {(tabData('shares').ledger || []).length === 0
                                ? <p className="text-slate-400 text-sm">No certificates recorded.</p>
                                : (
                                    <DataList rows={tabData('shares').ledger || []} rowKey="share_id" csvName="share-ledger" defaultSort={{ key: 'issued_on', dir: 'asc' }}
                                        filters={[{ key: 'txn_type', label: 'Type' }, { key: 'holder', label: 'Holder' }]}
                                        columns={[
                                            { key: 'certificate_no', label: 'Cert.' },
                                            { key: 'txn_type', label: 'Type' },
                                            { key: 'holder', label: 'Holder', render: (s) => <>{s.holder}{s.from_holder && <span className="text-slate-400"> ← {s.from_holder}</span>}</> },
                                            { key: 'from_holder', label: 'From', render: (s) => s.from_holder || '' },
                                            { key: 'shares', label: 'Shares', type: 'number', render: (s) => <span className="tabular-nums">{Number(s.shares).toLocaleString()}</span> },
                                            { key: 'issued_on', label: 'Issued', type: 'date', render: (s) => <span className="whitespace-nowrap">{fmtDate(s.issued_on)}</span> },
                                            { key: 'surrendered_on', label: 'Surrendered', type: 'date', render: (s) => <span className="whitespace-nowrap">{s.surrendered_on ? fmtDate(s.surrendered_on) : '—'}</span> },
                                        ]} />
                                )}
                        </>
                    )}

                    {['formation', 'bylaws', 'banking', 'state_filings', 'tax'].includes(tab) && (
                        <FiledTab tab={tab} data={tabData(tab)} obligations={obligations}
                            onUpload={() => { setForm({ tab }); setModal('document'); }} />
                    )}
                </div>
            </div>

            {/* ---- modals ---- */}
            {modal === 'officer' && (
                <Modal title="Add an officer or director" onClose={() => setModal(null)}>
                    <Input label="Office (President, Secretary, Director…)" value={form.office || ''}
                        onChange={e => setForm({ ...form, office: e.target.value })} />
                    <Input label="Person" value={form.person || ''}
                        onChange={e => setForm({ ...form, person: e.target.value })} />
                    <label className="block mb-3"><span className="text-sm text-slate-600">Serving as</span>
                        <select value={form.role_type} onChange={e => setForm({ ...form, role_type: e.target.value })}
                            className="mt-1 w-full p-2 border rounded">
                            <option value="officer">Officer</option><option value="director">Director</option>
                            <option value="both">Officer and director</option>
                        </select></label>
                    <Input label="Elected on" type="date" value={form.elected_on || ''}
                        onChange={e => setForm({ ...form, elected_on: e.target.value })} />
                    <button disabled={busy} onClick={() => submit('/api/corporate/officers', form)}
                        className="px-4 py-2 bg-slate-800 text-white rounded-md disabled:opacity-50">Save</button>
                </Modal>
            )}

            {modal === 'meeting' && (
                <Modal title="Record a meeting" onClose={() => setModal(null)}>
                    <label className="block mb-3"><span className="text-sm text-slate-600">Type</span>
                        <select value={form.meeting_type} onChange={e => setForm({ ...form, meeting_type: e.target.value })}
                            className="mt-1 w-full p-2 border rounded">
                            <option value="annual">Annual</option><option value="special">Special</option>
                            <option value="action_in_lieu">Action in lieu of meeting</option>
                            <option value="organizational">Organizational</option>
                        </select></label>
                    <Input label="Date held" type="date" value={form.held_on || ''}
                        onChange={e => setForm({ ...form, held_on: e.target.value })} />
                    <label className="block mb-3"><span className="text-sm text-slate-600">Status</span>
                        <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                            className="mt-1 w-full p-2 border rounded">
                            <option value="held">Held</option><option value="scheduled">Scheduled</option>
                        </select></label>
                    <Input label="Fiscal year reported on" type="number" value={form.fiscal_year || ''}
                        onChange={e => setForm({ ...form, fiscal_year: e.target.value })} />
                    <label className="block mb-3"><span className="text-sm text-slate-600">Summary / minutes</span>
                        <textarea rows="5" value={form.summary || ''} onChange={e => setForm({ ...form, summary: e.target.value })}
                            className="mt-1 w-full p-2 border rounded" /></label>
                    <button disabled={busy} onClick={() => submit('/api/corporate/meetings', form)}
                        className="px-4 py-2 bg-slate-800 text-white rounded-md disabled:opacity-50">Save</button>
                </Modal>
            )}

            {modal === 'resolution' && (
                <Modal title="Record a resolution" onClose={() => setModal(null)}>
                    <Input label="Title" value={form.title || ''}
                        onChange={e => setForm({ ...form, title: e.target.value })} />
                    <Input label="Adopted on" type="date" value={form.adopted_on || ''}
                        onChange={e => setForm({ ...form, adopted_on: e.target.value })} />
                    <Input label="Date of the document it authorises (optional)" type="date" value={form.document_date || ''}
                        onChange={e => setForm({ ...form, document_date: e.target.value })} />
                    <Input label="Counterparty" value={form.counterparty || ''}
                        onChange={e => setForm({ ...form, counterparty: e.target.value })} />
                    <Input label="Amount" type="number" step="0.01" value={form.amount || ''}
                        onChange={e => setForm({ ...form, amount: e.target.value })} />
                    <Input label="Document reference (where the source lives)" value={form.document_ref || ''}
                        onChange={e => setForm({ ...form, document_ref: e.target.value })} />
                    <label className="block mb-3"><span className="text-sm text-slate-600">Body</span>
                        <textarea rows="6" value={form.body_md || ''} onChange={e => setForm({ ...form, body_md: e.target.value })}
                            className="mt-1 w-full p-2 border rounded" /></label>
                    <button disabled={busy} onClick={() => submit('/api/corporate/resolutions', form)}
                        className="px-4 py-2 bg-slate-800 text-white rounded-md disabled:opacity-50">Save</button>
                </Modal>
            )}

            {modal === 'share' && (
                <Modal title="Record a certificate" onClose={() => setModal(null)}>
                    <Input label="Certificate number" type="number" value={form.certificate_no || ''}
                        onChange={e => setForm({ ...form, certificate_no: e.target.value })} />
                    <label className="block mb-3"><span className="text-sm text-slate-600">Type</span>
                        <select value={form.txn_type} onChange={e => setForm({ ...form, txn_type: e.target.value })}
                            className="mt-1 w-full p-2 border rounded">
                            <option value="issue">Issue</option><option value="transfer">Transfer</option>
                            <option value="cancel">Cancel</option>
                        </select></label>
                    <Input label="Holder" value={form.holder || ''}
                        onChange={e => setForm({ ...form, holder: e.target.value })} />
                    {form.txn_type === 'transfer' && (
                        <Input label="Transferred from" value={form.from_holder || ''}
                            onChange={e => setForm({ ...form, from_holder: e.target.value })} />
                    )}
                    <Input label="Number of shares" type="number" value={form.shares || ''}
                        onChange={e => setForm({ ...form, shares: e.target.value })} />
                    <Input label="Consideration" type="number" step="0.01" value={form.consideration || ''}
                        onChange={e => setForm({ ...form, consideration: e.target.value })} />
                    <Input label="Issued on" type="date" value={form.issued_on || ''}
                        onChange={e => setForm({ ...form, issued_on: e.target.value })} />
                    <button disabled={busy} onClick={() => submit('/api/corporate/shares', form)}
                        className="px-4 py-2 bg-slate-800 text-white rounded-md disabled:opacity-50">Save</button>
                </Modal>
            )}

            {modal === 'document' && (
                <Modal title="File a document" onClose={() => setModal(null)}>
                    <Input label="Title" value={form.title || ''}
                        onChange={e => setForm({ ...form, title: e.target.value })} />
                    <Input label="Document date" type="date" value={form.document_date || ''}
                        onChange={e => setForm({ ...form, document_date: e.target.value })} />
                    <label className="block mb-3"><span className="text-sm text-slate-600">File</span>
                        <input type="file" onChange={e => setForm({ ...form, file: e.target.files[0] })}
                            className="mt-1 w-full p-2 border rounded" /></label>
                    <button disabled={busy || !form.file} onClick={async () => {
                        const fd = new FormData();
                        fd.append('file', form.file);
                        fd.append('tab', form.tab);
                        fd.append('title', form.title || form.file.name);
                        if (form.document_date) fd.append('document_date', form.document_date);
                        setBusy(true);
                        try { await apiClient.post('/api/corporate/documents', fd, true); setModal(null); setForm({}); await load(); }
                        catch (e) { setError(e.message); }
                        finally { setBusy(false); }
                    }} className="px-4 py-2 bg-slate-800 text-white rounded-md disabled:opacity-50">Upload</button>
                </Modal>
            )}
        </div>
    );
}

function FiledTab({ tab, data, obligations, onUpload }) {
    const label = TABS.find(t => t.key === tab)?.label || tab;
    const related = (data.obligations || []);
    return (
        <>
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-slate-800">{label}</h2>
                <button onClick={onUpload} className="text-sm px-3 py-1 border rounded no-print">File a document</button>
            </div>
            {related.length > 0 && (
                <table className="w-full text-sm mb-6">
                    <tbody>
                        {related.map(o => (
                            <tr key={o.obligation_id} className="border-b border-slate-100">
                                <td className="py-2 font-medium text-slate-800">{o.title}</td>
                                <td className="py-2 text-slate-500">{o.authority}</td>
                                <td className="py-2 text-right whitespace-nowrap">
                                    {o.next_due ? `due ${fmtDate(o.next_due)}` : <span className="text-amber-700">needs a date</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            {(data.filed || []).length === 0
                ? <p className="text-slate-400 text-sm">Nothing filed under this tab yet.</p>
                : (
                    <ul className="text-sm">
                        {(data.filed || []).map(d => (
                            <li key={d.document_id} className="py-2 border-b border-slate-100 flex justify-between">
                                <span>{d.title} <span className="text-slate-400">· {d.original_name}</span></span>
                                <span className="text-slate-500 whitespace-nowrap">{fmtDate(d.document_date)}</span>
                            </li>
                        ))}
                    </ul>
                )}
        </>
    );
}
